import { DataRoomFile } from '@/types/documents/documents.types';
import {
  DocumentGridContainer,
  DocumentCard,
  DocumentCardHeader,
  DocumentIcon,
  DocumentActions,
  ActionButton,
  DocumentTitle,
  DocumentSynopsis,
  DocumentMeta,
  DocumentTags,
  Tag,
  DocumentVersionLabel,
  DocumentFormatLabel,
  LinkedBadge,
  LinkedIndicator,
} from './DocumentGrid.styles';
import { getFileIcon } from '@/utils/documents/documentHelpers';
import { formatDate, truncateText } from '@tw/tw-component-library';
import {
  FiCheck,
  FiDownload,
  FiEye,
  FiFolder,
  FiMessageSquare,
  FiSquare,
  FiTrash2,
} from 'react-icons/fi';
import { AskSession } from '@/apis/ask/askHistory/types';
import { useNavigate, useParams } from 'react-router-dom';

interface DocumentWithAskSessions extends DataRoomFile {
  askSessions?: AskSession[];
}

interface DocumentGridProps {
  documents: DocumentWithAskSessions[];
  onDocumentClick: (doc: DataRoomFile) => void;
  onDownload: (doc: DataRoomFile) => void;
  onGetSuggestions: (doc: DataRoomFile) => void;
  onMoveToCollection: (doc: DataRoomFile) => void;
  onDelete: (doc: DataRoomFile) => void;
  selectedDocumentIds?: string[];
  onToggleSelect?: (doc: DataRoomFile) => void;
  multiSelectEnabled?: boolean;
  getFileCollectionInfo: (fileId: string) => {
    name: string;
    isSmart: boolean;
  } | null;
  onView: (doc: DataRoomFile) => void;
  onLinkToAsk?: (doc: DataRoomFile) => void;
  workspaceId?: string;
  isCompact?: boolean;
}

export const DocumentGrid = ({
  documents,
  onDocumentClick,
  onDownload,
  onMoveToCollection,
  onDelete,
  selectedDocumentIds = [],
  onToggleSelect,
  multiSelectEnabled = false,
  getFileCollectionInfo,
  onView,
  onLinkToAsk,
  workspaceId,
  isCompact = false,
}: DocumentGridProps) => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const currentWorkspaceId = workspaceId || params.id;

  const handleAskSessionClick = (e: React.MouseEvent, askSession: AskSession) => {
    e.stopPropagation();
    if (currentWorkspaceId && askSession.askSessionId) {
      navigate(`/workspace/${currentWorkspaceId}/ask-session/${askSession.askSessionId}`);
    }
  };

  return (
    <DocumentGridContainer $isCompact={isCompact}>
      {documents.map((doc: DocumentWithAskSessions) => {
        const collectionInfo = getFileCollectionInfo(doc.dataRoomFileId);
        const linkedAskSessions = doc.askSessions || [];
        const isLinked = linkedAskSessions.length > 0;
        const isSelected = selectedDocumentIds.includes(doc.dataRoomFileId);

        return (
          <DocumentCard
            key={doc.dataRoomFileId}
            $isCompact={isCompact}
            onClick={() => onDocumentClick(doc)}
            style={
              isSelected
                ? {
                    outline: '2px solid #1976d2',
                    outlineOffset: '2px',
                    backgroundColor: '#f3f8ff',
                  }
                : undefined
            }
          >
            <DocumentCardHeader $isCompact={isCompact}>
              <DocumentIcon $isCompact={isCompact}>
                {getFileIcon(doc.name, doc.mimeType)}
              </DocumentIcon>
              <DocumentActions $isCompact={isCompact}>
                {multiSelectEnabled && onToggleSelect && (
                  <ActionButton
                    $isCompact={isCompact}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelect(doc);
                    }}
                    title={isSelected ? 'Unselect' : 'Select'}
                    style={{
                      backgroundColor: isSelected ? '#1976d2' : undefined,
                      color: isSelected ? 'white' : undefined,
                    }}
                  >
                    {isCompact ? (
                      isSelected ? (
                        <FiCheck />
                      ) : (
                        <FiSquare />
                      )
                    ) : isSelected ? (
                      '✓'
                    ) : (
                      '☐'
                    )}
                  </ActionButton>
                )}
                <ActionButton
                  $isCompact={isCompact}
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(doc);
                  }}
                  title="View"
                >
                  <FiEye />
                </ActionButton>
                <ActionButton
                  $isCompact={isCompact}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(doc);
                  }}
                  title="Download"
                >
                  <FiDownload />
                </ActionButton>
                <ActionButton
                  $isCompact={isCompact}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveToCollection(doc);
                  }}
                  title="Move to Collection"
                >
                  {isCompact ? <FiFolder /> : '📁'}
                </ActionButton>
                {onLinkToAsk && (
                  <ActionButton
                    $isCompact={isCompact}
                    onClick={(e) => {
                      e.stopPropagation();
                      onLinkToAsk(doc);
                    }}
                    title="Link to Ask Session"
                  >
                    {isCompact ? <FiMessageSquare /> : '💬'}
                  </ActionButton>
                )}
                <ActionButton
                  $isCompact={isCompact}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(doc);
                  }}
                  title="Delete"
                >
                  {isCompact ? <FiTrash2 /> : '🗑️'}
                </ActionButton>
              </DocumentActions>
            </DocumentCardHeader>
            <DocumentTitle $isCompact={isCompact}>
              {doc.name || 'Untitled Document'}
            </DocumentTitle>
            {doc.synopsis && (
              <DocumentSynopsis $isCompact={isCompact}>
                {truncateText(doc.synopsis, 100)}
              </DocumentSynopsis>
            )}
            {doc.version !== undefined && (
              <DocumentVersionLabel>
                Version: {doc.version}
              </DocumentVersionLabel>
            )}
            {doc.mimeType && (
              <DocumentFormatLabel>
                {doc.mimeType.split('/')[1]?.toUpperCase() || doc.mimeType}
              </DocumentFormatLabel>
            )}
            <DocumentMeta $isCompact={isCompact}>
              {doc.added && <div>{formatDate(doc.added)}</div>}
              {doc.readableSize && <div>{doc.readableSize}</div>}
            </DocumentMeta>
            <DocumentTags $isCompact={isCompact}>
              {/* //TODO: Add back in when we have a way to get the file type */}
              {/* <Tag variant="primary">{doc.dataRoomFileType || 'Workspace'}</Tag> */}
              {collectionInfo && (
                <Tag
                  variant={collectionInfo.isSmart ? 'warning' : 'secondary'}
                  title={
                    collectionInfo.isSmart
                      ? 'AI-Suggested Collection'
                      : 'Manual Collection'
                  }
                >
                  {collectionInfo.isSmart ? '✨ ' : '📁 '}
                  {collectionInfo.name}
                </Tag>
              )}
              {isLinked && (
                <LinkedBadge
                  variant="info"
                  title={
                    linkedAskSessions.length === 1
                      ? `Click to open: ${linkedAskSessions[0].title || 'Ask Session'}`
                      : `Linked to ${linkedAskSessions.length} ask sessions. Click to open the first one.`
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      linkedAskSessions.length > 0 &&
                      linkedAskSessions[0].askSessionId
                    ) {
                      handleAskSessionClick(e, linkedAskSessions[0]);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  💬 Linked
                  {linkedAskSessions.length > 1 &&
                    ` (${linkedAskSessions.length})`}
                </LinkedBadge>
              )}
            </DocumentTags>
            {isLinked && (
              <LinkedIndicator
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    linkedAskSessions.length > 0 &&
                    linkedAskSessions[0].askSessionId
                  ) {
                    handleAskSessionClick(e, linkedAskSessions[0]);
                  }
                }}
                style={{ cursor: 'pointer' }}
                title={
                  linkedAskSessions.length === 1
                    ? `Click to open: ${linkedAskSessions[0].title || 'Ask Session'}`
                    : `Click to open the first ask session. Linked to: ${linkedAskSessions.map((s) => s.title || 'Unnamed').join(', ')}`
                }
              >
                <span>💬</span>
                <span>
                  {linkedAskSessions.length === 1
                    ? `Linked to "${linkedAskSessions[0].title || 'Ask Session'}"`
                    : `Linked to ${linkedAskSessions.length} ask sessions`}
                </span>
              </LinkedIndicator>
            )}
          </DocumentCard>
        );
      })}
    </DocumentGridContainer>
  );
};
